import React, { FC, cloneElement, isValidElement } from 'react';

import { StyledTab, StyledTabPanel, TabsContainer, TabsWrapper, Underline } from './tabs.styled';
import { ITabListProps, ITabPanelProps, ITabProps } from './types';

interface ITabPropsAndOnClick extends ITabProps {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const TabList: FC<ITabListProps> = ({ children, selectedIndex, onChange }) => {
  if (!children || React.Children.count(children) <= 0) {
    return null;
  }

  // calculating percentage of tab width
  const tabSlideWidth = 100 / children.length;

  const tabs = React.Children.map(children, (child, indx) => {
    if (!isValidElement(child)) {
      return child;
    }

    const onClick = (e: React.ChangeEvent<{}>) => {
      onChange(e, indx);
    };

    return cloneElement<ITabPropsAndOnClick>(child, { onClick });
  });

  return (
    <TabsContainer>
      <TabsWrapper role="tablist">{tabs}</TabsWrapper>
      <Underline tabSlideWidth={tabSlideWidth} idx={selectedIndex} />
    </TabsContainer>
  );
};

export const Tab: FC<ITabPropsAndOnClick> = ({
  id,
  label,
  disabled,
  onClick,
  selected,
  'aria-controls': ariaControls,
  'data-testid': dataTestId,
}) => {
  return (
    <StyledTab
      id={id}
      role="tab"
      $selected={selected}
      onClick={onClick}
      disabled={disabled}
      aria-selected={selected}
      aria-controls={ariaControls}
      data-testid={dataTestId}
    >
      {label}
    </StyledTab>
  );
};

export const TabPanel: FC<ITabPanelProps> = ({
  id,
  selected,
  children,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
}) => {
  return (
    <StyledTabPanel
      id={id}
      key={`tab-panel_${id}`}
      hidden={!selected}
      $selected={selected}
      role="tabpanel"
      tabIndex={0}
      aria-labelledby={ariaLabelledBy}
      data-testid={dataTestId}
    >
      {children}
    </StyledTabPanel>
  );
};
